import React from "react";
import { notification } from "../../utils/Notifications.tsx";
import { updateDocumentType } from "../../services/documentTypes.service.ts";

interface DocumentTypeToEdit {
  id: number;
  name: string;
  obligatory: boolean;
  maxDayToUpload?: number;
}

interface EditDocumentTypeProps {
  data: DocumentTypeToEdit;
  closeFn: () => void;
}

const EditDocumentType: React.FC<EditDocumentTypeProps> = ({ data, closeFn }) => {
  const [name, setName] = React.useState(data.name);
  const [obligatory, setObligatory] = React.useState(data.obligatory);
  const [maxDayToUpload, setMaxDayToUpload] = React.useState<number>(data.maxDayToUpload ?? 0);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setName(data.name);
    setObligatory(data.obligatory);
    setMaxDayToUpload(data.maxDayToUpload ?? 0);
  }, [data.id, data.name, data.obligatory, data.maxDayToUpload]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      notification("Error", "El nombre es requerido", "danger");
      return;
    }
    const days = Math.min(30, Math.max(0, Number(maxDayToUpload) || 0));
    setSaving(true);
    try {
      await updateDocumentType(data.id, { name: name.trim(), obligatory, maxDayToUpload: days });
      notification("Éxito", "Tipo de documento actualizado correctamente", "success");
      closeFn();
    } catch (error: any) {
      notification(
        "Error",
        error?.message || "Error al actualizar el tipo de documento",
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="form-label">Nombre</label>
        <input
          type="text"
          className="form-control"
          placeholder="Nombre del tipo de documento"
          value={name}
          onChange={(e) => setName(e.target.value)}
          readOnly={data.id <= 23}
          disabled={data.id <= 23}
        />
        {data.id <= 23 && (
          <small className="text-muted">El nombre no se puede modificar para este tipo de documento.</small>
        )}
      </div>
      <div className="mb-3">
        <label className="form-label">Días máximos para subir</label>
        <input
          type="number"
          min={0}
          max={30}
          className="form-control"
          placeholder="0"
          value={maxDayToUpload === 0 ? "" : maxDayToUpload}
          onChange={(e) => setMaxDayToUpload(e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0)}
        />
        <small className="text-muted">Entre 0 y 30 días</small>
      </div>
      <div className="mb-3 form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id="obligatory-edit"
          checked={obligatory}
          onChange={(e) => setObligatory(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="obligatory-edit">
          Obligatorio
        </label>
      </div>
      <div className="d-flex justify-content-end">
        <button type="button" className="btn btn-secondary me-2" onClick={closeFn}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-success" disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </form>
  );
};

export default EditDocumentType;
